import { forwardRef, ReactNode } from "react";

import { LinearProgress, Stack, StackProps, Theme, SxProps, useTheme, linearProgressClasses } from "@mui/material";

import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";

const MINIMUM_PROGRESS = 1;

export type FormThermometerProps = StackProps & {
  label: ReactNode;
  percentage?: number;
  components?: {
    progress?: {
      sx?: SxProps<Theme>;
    };
  };
  backgroundContent?: ReactNode;
};

export const FormThermometer = forwardRef<HTMLDivElement, FormThermometerProps>(
  ({ label, percentage = 0, components, sx, backgroundContent, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.thermometer) {
      return <InvalidThemeErrorMessage />;
    }

    const progress = Math.min(Math.max(percentage, MINIMUM_PROGRESS), 100);
    const roundedPercentage = Math.round(percentage);

    const styles = mergeSx(
      {
        padding: 1.5,
        backgroundColor: theme.palette.formsComponents.thermometer.backgroundColor,
        borderRadius: theme.radius(1),
      },
      sx
    );

    const progressStyles = mergeSx(
      {
        position: "relative",
        zIndex: 1,
        height: "10px",
        borderRadius: 9999,
        backgroundColor: theme.palette.formsComponents.thermometer.progressTrackBackgroundColor,
        [`& .${linearProgressClasses.bar}`]: {
          borderRadius: 9999,
          backgroundColor: theme.palette.formsComponents.thermometer.progressBarBackgroundColor,
        },
      },
      components?.progress?.sx
    );

    return (
      <Stack ref={ref} gap={1} sx={styles} {...rest}>
        {backgroundContent}
        <Stack direction="row" justifyContent="space-between" gap={2} sx={{ position: "relative", zIndex: 1 }}>
          <FormTypography color={theme.palette.formsComponents.thermometer.textColor} variant="subtitle2">
            {label}
          </FormTypography>
          <FormTypography color={theme.palette.formsComponents.thermometer.textColor} variant="subtitle2">
            {roundedPercentage}%
          </FormTypography>
        </Stack>
        <LinearProgress value={progress} variant="determinate" sx={progressStyles} />
      </Stack>
    );
  }
);

FormThermometer.displayName = "FormThermometer";
